import { motion } from 'framer-motion';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { H1, BodyLarge } from '../components/ui/Typography';
import { Link } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

export default function Press() {
  const [currentPage, setCurrentPage] = useState(1);
  const articlesPerPage = 5;

  const pressReleases = [
    {
      title: "RentFi Makes Real Estate Investment Available to Everyone",
      summary: "RentFi has successfully launched its blockchain-based real estate investment platform, making property investment as simple as buying a token. Through its $RENT token, now actively trading, investors worldwide can earn rental income without the traditional hurdles of property ownership.",
      link: "/press/real-estate-investment-for-everyone",
      ariaLabel: "Read press release about RentFi's platform launch and token trading"
    },
    {
      title: "RentFi — Revolutionizing Real Estate Investment with Blockchain Technology",
      summary: "RentFi announces the launch of its token on February 5, 2025, introducing a groundbreaking approach to real estate investment through blockchain technology.",
      link: "/press/revolutionizing-real-estate-investment",
      ariaLabel: "Read press release about RentFi's token launch and blockchain real estate investment approach"
    },
    {
      title: "RentFi Reveals Groundbreaking Yield Optimization Model for Property Investments",
      summary: "RentFi Foundation unveiled its innovative yield optimization framework, demonstrating how understanding passive income mechanisms can revolutionize real estate returns. This announcement showcases RentFi's proprietary approach to maximizing different passive income streams ahead of its token launch in early 2025.",
      link: "/press/yield-optimization-model",
      ariaLabel: "Read press release about RentFi's yield optimization framework for property investments"
    },
    {
      title: "RentFi Announces First Property Acquisition Strategy in the Colombian Market",
      summary: "RentFi reveals its strategic entry into the Colombian real estate market, marking a significant milestone in delivering ways of generating income for global investors. The announcement details the platform's first targeted property acquisition in Medellín.",
      link: "/press/colombian-market-strategy",
      ariaLabel: "Read press release about RentFi's property acquisition strategy in Colombia"
    },
    {
      title: "RentFi Unveils Passive Income Application for Real Estate Investment",
      summary: "RentFi Foundation today revealed details of its innovative passive income application, designed to revolutionize real estate investment through blockchain technology.",
      link: "/press/passive-income-application",
      ariaLabel: "Read press release about RentFi's passive income application launch"
    },
    {
      title: "RentFi Emerges to Transform High-Yield Properties Investment Through Blockchain",
      summary: "RentFi Foundation unveils its innovative approach to democratizing real estate investment, announcing development of a blockchain-based platform that will make high-yield property investments accessible to everyone.",
      link: "/press/transform-high-yield-properties",
      ariaLabel: "Read press release about RentFi's approach to transforming high-yield property investments"
    }
  ];

  // Calculate pagination
  const totalPages = Math.ceil(pressReleases.length / articlesPerPage);
  const indexOfLastArticle = currentPage * articlesPerPage;
  const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
  const currentArticles = pressReleases.slice(indexOfFirstArticle, indexOfLastArticle);

  return (
    <>
      <Helmet>
        <title>RentFi | Newsroom</title>
        <meta 
          name="description" 
          content="Stay updated with RentFi's latest announcements, press releases, and news about our blockchain-powered real estate investment platform. Follow our journey in revolutionizing property investment." 
        />
        <meta 
          name="keywords" 
          content="RentFi news, press releases, real estate investment news, blockchain real estate updates, property investment announcements, RentFi media coverage" 
        />
        <link rel="canonical" href="https://rentfi.io/press" />
      </Helmet>
      <div className="min-h-screen bg-gray-50 py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="text-center mb-16">
              <H1 className="text-primary mb-4">Press Releases</H1>
              <BodyLarge className="text-gray-600">
                Latest news and announcements from RentFi
              </BodyLarge>
            </div>

            <div className="space-y-8 mb-12">
              {currentArticles.map((press, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="bg-white rounded-xl shadow-lg p-8"
                >
                  <h2 className="text-2xl font-bold text-primary mb-4">{press.title}</h2>
                  <p className="text-gray-600 mb-4">{press.summary}</p>
                  <Link 
                    to={press.link} 
                    className="text-secondary hover:text-secondary-dark font-semibold inline-flex items-center"
                    aria-label={press.ariaLabel}
                  >
                    Read More
                    <ChevronRightIcon className="w-5 h-5 ml-1" />
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center space-x-4">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Go to previous page of press releases"
                >
                  <ChevronLeftIcon className="w-5 h-5" />
                </button>
                <span className="text-gray-600">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="p-2 rounded-lg hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  aria-label="Go to next page of press releases"
                >
                  <ChevronRightIcon className="w-5 h-5" />
                </button>
              </div>
            )}
          </motion.div>
        </div>
      </div>
    </>
  );
}